import React, { useState, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { useElements } from '@stripe/react-stripe-js';
import { LoadingView, ServerSideErrorModal } from '@web/components';
import { PromoPill } from '@web/molecules';
import { BackgroundColorContainer, HideOnMobile } from '@web/atoms';
import { useOffer } from '../../offer';
import { useCampaign } from '../../campaign';
import { ApiResult, wrapErrorWithResult } from '../../api';
import { usePets } from '../../pets';
import { useReporter } from '../../reporter';
import { SHOW_PROMO_CODE_KEY, useExperiment } from '../../experiments';
import { useOrderList } from '../../orders';
import {
  useSessionCustomer,
  useCartProducts,
  useCartSubscriptionProducts,
  useAddress,
} from '../../customer';
import { CheckoutForm } from '../CheckoutForm';
import { CheckoutHeader } from '../CheckoutHeader';
import { CHECKOUT_STEPS } from '../CheckoutHeader/constants';
import { UserReturnModal } from '../UserReturnModal';
import {
  usePayments,
  STRIPE_REDIRECT_URL,
  useStripe,
  getPendingIntentTransactionId,
  getSetupIntentTransactionId,
} from '../../payment';
import { OnboardingContext } from '../OnboardingFlowContainer/context';
import { ACTIONS } from '../OnboardingFlowContainer/constants';
import { ERROR_MESSAGE } from './constants';
import {
  userCheckoutPurchased,
  userCompletedOrder,
  billingInformationCompleted,
} from './events';

export const CheckoutContainer = ({
  headerShippingAddress,
  labelFirstName,
  labelLastName,
  labelAddress,
  labelApt,
  labelZipCode,
  labelCity,
  labelState,
  stateOptions,
  labelPhoneNumber,
  labelSMSUpdates,
  labelSMSTerms,
  headerAccountInfo,
  labelEmail,
  labelPassword,
  labelConfirmation,
  headerIDMeForm,
  message,
  buttonImage,
  buttonAlt,
  headlineBillingInformation,
  orderSummary,
  recurringOrders,
  ctaText,
}) => {
  const offer = useOffer();
  const {
    send,
    pricePeriod,
    isAppSession,
    isReturningCustomerWithMealPlan,
    setReturningCustomerWithMealPlan,
    flags: { defaultCadenceOnboarding, extrasPageCRO },
  } = useContext(OnboardingContext);
  const {
    stripe,
    stripeError,
    clientSecret,
    updateIntent,
    confirmPaymentIntent,
    createIntent,
    confirmSetupIntent,
    setFreeOrder,
    isFreeOrder,
  } = useStripe();
  const elements = useElements();
  const reporter = useReporter();
  const { value: showPromoCode } = useExperiment(SHOW_PROMO_CODE_KEY, false);
  const { listOrders } = useOrderList();
  const { campaignId, setCampaignId } = useCampaign();
  const petsApi = usePets();
  const customerApi = useSessionCustomer();
  const addressApi = useAddress(customerApi.customer?.id); // TODO: remove opt chain when Mirage fixed
  const paymentsApi = usePayments(customerApi.customer?.id);
  const { productsListApi } = useCartProducts();
  const { cartApi, subscriptionProductsListApi } =
    useCartSubscriptionProducts();
  const [errorMessageInfo, setErrorMessageInfo] = useState(
    stripeError ? stripeError.message : null,
  );
  const [isRevalidatingCart, setRevalidatingCart] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const closeErrorModal = () => setErrorMessageInfo(null);
  const paymentTypeEvent = useRef(null);

  const handlePaymentIntent = async () => {
    let response;
    if (!isFreeOrder) {
      response = await updateIntent(cartApi.cart.cart_id, elements);
    }

    if (!response?.success) {
      response = await createIntent(cartApi.cart.cart_id);
    }
    return response;
  };

  const onSubmit = async data => {
    // Validate payment element fields
    let paymentIntentsResponse;

    setFreeOrder(cartApi.cart.total <= 0);
    paymentIntentsResponse = await handlePaymentIntent();
    const elementsSubmit = await ApiResult.flatMapAsync(
      paymentIntentsResponse,
      () => elements.submit(),
    );

    const submitResult = elementsSubmit?.error
      ? wrapErrorWithResult(elementsSubmit?.error)
      : ApiResult.success();

    const updateCustomerResult = await ApiResult.flatMapAsync(
      submitResult,
      () =>
        ApiResult.callAsync(() =>
          customerApi.updateCustomer({
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            is_sms: data.smsUpdates,
          }),
        ),
    );
    const setPasswordResult = await ApiResult.flatMapAsync(
      updateCustomerResult,
      () =>
        customerApi.createPassword({
          new_password: data.new_password,
          confirm_password: data.confirm_password,
        }),
    );
    const pendingIntentTransactionId = Array.isArray(
      paymentIntentsResponse?.payload,
    )
      ? getPendingIntentTransactionId(paymentIntentsResponse?.payload)
      : isFreeOrder
      ? getSetupIntentTransactionId(paymentIntentsResponse?.payload)
      : clientSecret;
    const paymentIntentResult = await ApiResult.flatMapAsync(
      setPasswordResult,
      () =>
        isFreeOrder
          ? confirmSetupIntent({
              stripe,
              elements,
              customer: {
                email: data.email,
              },
              address: {
                phone: data.phoneNumber,
              },
              clientSecret: pendingIntentTransactionId,
            })
          : confirmPaymentIntent({
              stripe,
              elements,
              clientSecret: pendingIntentTransactionId,
              options: {
                confirmParams: {
                  payment_method_data: {
                    billing_details: {
                      email: data.email,
                      phone: data.phoneNumber,
                      address: { country: 'US' },
                    },
                  },
                  return_url: STRIPE_REDIRECT_URL,
                },
                redirect: 'if_required',
              },
            }),
    );

    const checkoutResult = await ApiResult.flatMapAsync(
      paymentIntentResult,
      () =>
        customerApi.performCheckout([pendingIntentTransactionId], campaignId),
    );

    const isTestCadence = defaultCadenceOnboarding;
    ApiResult.match(checkoutResult, {
      success: async () => {
        const orders = await listOrders();
        if (campaignId) setCampaignId(null);
        send(ACTIONS.toConfirmation({ orderId: orders[0].id }));
        try {
          await reporter.withPrefix('').tag(userCompletedOrder(orders));
        } catch (error) {
          reporter.error(error);
        }
        try {
          await reporter.tag(
            userCheckoutPurchased(
              orders,
              paymentTypeEvent.current,
              isTestCadence,
            ),
          );
        } catch (error) {
          reporter.error(error);
        }
      },
      error: {
        server: () => {
          setErrorMessageInfo(ERROR_MESSAGE.server);
        },
        paymentInvalid: error => {
          setErrorMessageInfo(
            ERROR_MESSAGE.stripePaymentInvalid(error?.message),
          );
          const paymentElement = elements.getElement('payment');
          if (paymentElement) paymentElement.focus();
        },
        paymentInsufficientFunds: error => {
          setErrorMessageInfo(ERROR_MESSAGE.paymentInsufficientFunds);
        },
        unableToProcessOrder: () => {
          setErrorMessageInfo(ERROR_MESSAGE.unableToProcessOrder);
        },
        checkoutAddress: () => {
          setErrorMessageInfo(ERROR_MESSAGE.address);
        },
        passwordNotSet: () => {
          setErrorMessageInfo(ERROR_MESSAGE.passwordNotSet);
        },
        client: () => {
          setErrorMessageInfo(ERROR_MESSAGE.server);
        },
      },
    });

    return checkoutResult;
  };

  const applyPromoCodeHandler = async ({ newPromoCode }) => {
    setRevalidatingCart(true);
    const validateOfferCodeResult = await offer.validateOfferCode(newPromoCode);

    const newPromoCodeResult = await ApiResult.flatMapAsync(
      validateOfferCodeResult,
      () => offer.updateOfferCode(newPromoCode),
    );

    const cart = await cartApi.refreshCart();
    setFreeOrder(cart.payload.total <= 0);
    await handlePaymentIntent();

    setRevalidatingCart(false);
    return newPromoCodeResult;
  };
  const removePromoCodeHandler = async () => {
    setRevalidatingCart(true);
    const validateOfferCodeResult = await offer.validateOfferCode(
      offer.defaultOfferCode,
    );
    const removePromoCodeResult = await ApiResult.flatMapAsync(
      validateOfferCodeResult,
      () => offer.updateOfferCode(offer.defaultOfferCode),
    );

    const cart = await cartApi.refreshCart();
    setFreeOrder(cart.payload.total <= 0);
    await handlePaymentIntent();

    setRevalidatingCart(false);
    return removePromoCodeResult;
  };

  const refreshCart = async cartOptions => {
    setRevalidatingCart(true);
    const cart = await cartApi.refreshCart(cartOptions);
    setRevalidatingCart(false);
    return cart;
  };

  const deletePet = async petId => {
    const deleteResponse = await petsApi.deletePet(petId);
    return deleteResponse;
  };

  const isLoading =
    addressApi.isLoading ||
    cartApi.isLoading ||
    customerApi.isLoading ||
    paymentsApi.isLoading ||
    petsApi.isLoading ||
    productsListApi.isLoading ||
    subscriptionProductsListApi.isLoading;

  useEffect(() => {
    if (!isLoading) {
      const paymentElement = elements?.getElement('payment');
      if (paymentElement) {
        const debouncedPaymentElementComplete = debounce(event => {
          const completedForm = event?.complete;
          const eventType = event?.value?.type;
          if (completedForm) {
            paymentTypeEvent.current = eventType;
            reporter.tag(billingInformationCompleted(eventType));
          }
        }, 250);
        paymentElement.on('change', debouncedPaymentElementComplete);
      }
    }
  }, [elements, reporter, isLoading]);

  useEffect(() => {
    if (isReturningCustomerWithMealPlan && petsApi.pets.length > 1) {
      setShowModal(true);
      setReturningCustomerWithMealPlan(false);
    }
  }, [
    isReturningCustomerWithMealPlan,
    setReturningCustomerWithMealPlan,
    petsApi.pets,
  ]);

  if (isLoading) return <LoadingView />;

  if (cartApi.error) return <LoadingView variant="error" />;

  const rightHeaderSegment = (
    <HideOnMobile>
      <PromoPill />
    </HideOnMobile>
  );
  const onCloseModal = () => setShowModal(false);

  let EXTRA_STEPS_CONDITIONAL = CHECKOUT_STEPS;

  if (extrasPageCRO === 'no_page') {
    EXTRA_STEPS_CONDITIONAL = CHECKOUT_STEPS.filter(item => item !== 'Extras');
  }

  return (
    <BackgroundColorContainer $bgColor="background.base">
      <CheckoutHeader
        currentStep={
          EXTRA_STEPS_CONDITIONAL[EXTRA_STEPS_CONDITIONAL.length - 1]
        } //* Last step will always be Checkout Page
        rightSegment={rightHeaderSegment}
        steps={EXTRA_STEPS_CONDITIONAL}
      />
      <CheckoutForm
        headerShippingAddress={headerShippingAddress}
        labelFirstName={labelFirstName}
        labelLastName={labelLastName}
        labelAddress={labelAddress}
        labelApt={labelApt}
        labelZipCode={labelZipCode}
        labelCity={labelCity}
        labelState={labelState}
        stateOptions={stateOptions}
        labelPhoneNumber={labelPhoneNumber}
        labelSMSUpdates={labelSMSUpdates}
        labelSMSTerms={labelSMSTerms}
        headerAccountInfo={headerAccountInfo}
        labelEmail={labelEmail}
        labelPassword={labelPassword}
        labelConfirmation={labelConfirmation}
        headerIDMeForm={headerIDMeForm}
        message={message}
        buttonAlt={buttonAlt}
        buttonImage={buttonImage}
        headlineBillingInformation={headlineBillingInformation}
        ctaText={ctaText}
        onSubmit={onSubmit}
        address={addressApi.address}
        updateAddress={addressApi.update}
        cart={cartApi.cart}
        isValidating={cartApi.isValidating}
        isRevalidatingCart={isRevalidatingCart}
        refreshCart={refreshCart}
        customer={customerApi.customer}
        pets={petsApi.pets}
        deletePet={deletePet}
        products={productsListApi.products}
        subscriptionProducts={subscriptionProductsListApi.products}
        recurringOrders={recurringOrders}
        orderSummary={orderSummary}
        period={pricePeriod}
        showPromoCode={showPromoCode}
        showAddPupButton={!isAppSession}
        applyPromoCode={applyPromoCodeHandler}
        removePromoCode={removePromoCodeHandler}
        isDefaultPromoCode={offer.code === offer.defaultOfferCode}
        currentOfferCode={offer.code}
        defaultOfferCode={offer.defaultOfferCode}
        isFreeOrder={isFreeOrder}
      />
      <UserReturnModal showModal={showModal} onCloseModal={onCloseModal} />
      <ServerSideErrorModal
        shown={errorMessageInfo !== null}
        headerText={errorMessageInfo?.title ?? ''}
        errorMessage={errorMessageInfo?.message ?? ''}
        onCloseModal={closeErrorModal}
        variant={'secondary'}
        showCloseButton={true}
      />
    </BackgroundColorContainer>
  );
};

CheckoutContainer.displayName = 'CheckoutContainer';
CheckoutContainer.propTypes = {
  headerShippingAddress: PropTypes.string.isRequired,
  labelFirstName: PropTypes.string.isRequired,
  labelLastName: PropTypes.string.isRequired,
  labelAddress: PropTypes.string.isRequired,
  labelApt: PropTypes.string.isRequired,
  labelZipCode: PropTypes.string.isRequired,
  labelCity: PropTypes.string,
  labelState: PropTypes.string.isRequired,
  stateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ),
  labelPhoneNumber: PropTypes.string.isRequired,
  labelSMSUpdates: PropTypes.string.isRequired,
  labelSMSTerms: PropTypes.string.isRequired,
  headerAccountInfo: PropTypes.string.isRequired,
  labelEmail: PropTypes.string.isRequired,
  labelPassword: PropTypes.string.isRequired,
  labelConfirmation: PropTypes.string.isRequired,
  headerIDMeForm: PropTypes.string.isRequired,
  message: PropTypes.node,
  buttonImage: PropTypes.string.isRequired,
  buttonAlt: PropTypes.string.isRequired,
  headlineBillingInformation: PropTypes.string.isRequired,
  orderSummary: PropTypes.object.isRequired,
  recurringOrders: PropTypes.shape({
    foodPacksText: PropTypes.string.isRequired,
  }).isRequired,
  ctaText: PropTypes.string.isRequired,
};
